import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { ExternalLink, ArrowRight, Info } from "lucide-react";
import api from "../lib/api";

export default function StepUpBooking() {
  const navigate = useNavigate();
  const [steps, setSteps] = useState([]);
  const [faq, setFaq] = useState([]);
  const [ctaUrl, setCtaUrl] = useState("https://www.stepupforstudents.org/");

  useEffect(() => {
    api.get("/site-content").then((r) => {
      const c = r.data || {};
      setSteps(c.step_up_steps || []);
      setFaq(c.step_up_faq || []);
      if (c.step_up_cta_url) setCtaUrl(c.step_up_cta_url);
    }).catch(() => {});
  }, []);

  const WORKFLOW = ["Choose Experience", "Review Details", "View Step Up Instructions", "Reserve Funds in EMA", "Contact H.E.A.", "Confirm Availability", "Finalize Registration"];

  return (
    <PublicLayout>
      <section className="bg-navy topo-bg">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <span className="eyebrow text-gold">Step Up For Students</span>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-extrabold text-white lg:text-5xl">Book Your H.E.A. Experience With Step Up For Students</h1>
          <p className="mt-4 max-w-2xl text-white/70">Families using eligible Step Up For Students scholarship funds can follow these steps to find Homeschool Enrichment Academy in the Education Market Assistant (EMA) Marketplace and request an approved service.</p>
        </div>
      </section>

      {/* STEPS */}
      <section className="mx-auto max-w-4xl px-5 py-16 lg:px-8">
        <h2 className="font-display text-2xl font-bold text-navy">Step-by-step booking instructions</h2>
        <div className="mt-8 space-y-4">
          {steps.map((s, i) => (
            <div key={i} data-testid={`stepup-step-${i + 1}`} className="flex gap-5 rounded-2xl border border-slate-200 bg-white p-6">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brandblue font-display text-lg font-bold text-white">{i + 1}</div>
              <div>
                <h3 className="font-display text-lg font-bold text-navy">{s.title}</h3>
                <p className="mt-1 text-slateBrand">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 flex items-start gap-3 rounded-xl bg-gold/10 p-4 text-sm text-[#8A5F00]">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          Reserving funds does not automatically guarantee a place on a trip. Registration is finalized after H.E.A. confirms availability and scheduling.
        </div>
      </section>

      {/* WORKFLOW */}
      <section className="border-y border-slate-200 bg-cloud">
        <div className="mx-auto max-w-5xl px-5 py-14 lg:px-8">
          <h2 className="text-center font-display text-2xl font-bold text-navy">Your booking journey</h2>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
            {WORKFLOW.map((w, i) => (
              <React.Fragment key={w}>
                <span className="rounded-full border border-slate-200 bg-white px-4 py-2 font-display text-sm font-semibold text-navy">{w}</span>
                {i < WORKFLOW.length - 1 && <ArrowRight className="h-4 w-4 text-slate-300" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-5xl px-5 py-16 lg:px-8">
        <div className="rounded-3xl bg-navy p-10 text-center lg:p-14">
          <h2 className="font-display text-3xl font-bold text-white">Ready to Start Your Next Learning Adventure?</h2>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <a href={ctaUrl} target="_blank" rel="noopener noreferrer" data-testid="stepup-open-btn">
              <Button className="h-13 w-full bg-gold px-8 py-6 font-display text-base font-semibold text-navy hover:bg-gold/90 sm:w-auto">
                Open Step Up For Students <ExternalLink className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Button onClick={() => navigate("/contact")} data-testid="stepup-contact-btn" variant="outline"
              className="h-13 border-white/30 bg-white/10 px-8 py-6 font-display text-base font-semibold text-white hover:bg-white/20">
              Contact H.E.A. to Finalize Registration
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-5 pb-20 lg:px-8">
        <h2 className="font-display text-2xl font-bold text-navy">Important information</h2>
        <Accordion type="single" collapsible className="mt-6 space-y-3">
          {faq.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`stepup-faq-${i}`} className="rounded-xl border border-slate-200 bg-white px-5">
              <AccordionTrigger className="font-display text-left font-semibold text-navy hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-slateBrand">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </PublicLayout>
  );
}
